import { useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Button from "@/components/button";
import Input from "@/components/input";
import PasswordInput from "@/components/password-input";
import { Checkbox } from "@/components/ui/checkbox";
import { ToggleGroup, ToggleGroupItem } from "@/components/toggle-group";
import { useAuth } from "@/context/auth";
import { decideNextRoute } from "@/utils/auth-flow";
import { env } from "@/utils";
import { BASE_URL } from "@/queries/api/apiUtils";
import { getWorkerInvitePrefill } from "@/queries/api/workerInviteApi";
import { updateCommunicationPreferences } from "@/queries/api/communicationPrefsApi";
import { captureEvent } from "@/lib/analytics/posthog";
import {
  signupStartedEvent,
  signupAccountCreatedEvent,
} from "@/lib/analytics/events";

// Whether signup is gated behind an invite code. Server-side enforcement is
// independent; this only toggles the invite field + express-interest fallback UI.
const REQUIRE_INVITE_CODE = env.VITE_REQUIRE_INVITE_CODE === "true";

export const Route = createFileRoute("/auth/_layout/signup")({
  beforeLoad({ context }) {
    if (context.auth?.isAuthenticated && context.auth.user) {
      const user = context.auth.user;
      const userRole = user.role;

      const nextRoute = decideNextRoute({
        role: user.role,
        emailVerified: user.emailVerified,
        phoneVerified: user.phoneVerified,
        onboardingCompleted: user.onboardingCompleted,
      });

      if (nextRoute.startsWith("/app/")) {
        throw redirect({
          to: nextRoute,
          params: { role: userRole },
        });
      } else {
        throw redirect({ to: nextRoute });
      }
    }
  },
  component: SignupPage,
});

function SignupPage() {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const navigate = useNavigate();

  const SignupSchema = useMemo(
    () =>
      z
        .object({
          firstname: z
            .string()
            .min(1)
            .refine((val) => val.trim().length > 0, {
              message: t("validation.firstNameBlank"),
            }),
          lastname: z
            .string()
            .min(1)
            .refine((val) => val.trim().length > 0, {
              message: t("validation.lastNameBlank"),
            }),
          email: z.string().email({ message: t("validation.invalidEmail") }),
          phone: z.string().regex(/^\+?[1-9]\d{1,14}$/, {
            message: t("validation.invalidPhone"),
          }),
          role: z.union([z.literal("worker"), z.literal("employer")]),
          password: z
            .string()
            .min(8, { message: t("validation.passwordMinLength") })
            .regex(/[A-Z]/, { message: t("validation.passwordUppercase") })
            .regex(/[a-z]/, { message: t("validation.passwordLowercase") })
            .regex(/[0-9]/, { message: t("validation.passwordNumber") })
            .regex(/[@$!%*?&]/, {
              message: t("validation.passwordSpecial"),
            })
            .regex(/^[A-Za-z0-9@$!%*?&]+$/, {
              message: t("validation.passwordAllowedChars"),
            }),
          password2: z.string(),
          // Required when signup is gated, unless the person arrived through a
          // restaurant's worker-invite link (checked in superRefine below).
          inviteCode: z.string().optional(),
          workerInviteToken: z.string().optional(),
          // SMS opt-in: optional (consent is not a condition of use) and must
          // default to unchecked for Twilio toll-free compliance.
          smsConsent: z.boolean().optional(),
        })
        .refine((data) => data.password === data.password2, {
          message: t("validation.passwordsDoNotMatch"),
          path: ["password2"],
        })
        .superRefine((data, ctx) => {
          if (
            REQUIRE_INVITE_CODE &&
            !data.workerInviteToken &&
            !data.inviteCode?.trim()
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("validation.inviteCodeRequired"),
              path: ["inviteCode"],
            });
          }
        }),
    [t],
  );

  const methods = useForm({
    resolver: zodResolver(SignupSchema),
    defaultValues: {
      role: "worker" as const,
      smsConsent: false,
    },
  });
  const [error, setError] = useState("");
  // Set when arriving from a worker-invite link (?invite=<token>): shows a small
  // "invited by <restaurant>" note and pre-fills the form. `invitePrefilled`
  // gates the banner; `invitedBy` is the restaurant name (may be null). The
  // token is sent with registration so the BE skips the invite code and puts
  // the new worker on the restaurant's roster.
  const [invitePrefilled, setInvitePrefilled] = useState(false);
  const [invitedBy, setInvitedBy] = useState<string | null>(null);
  // The email the invite was sent to. Signup must use it (the BE enforces this),
  // so the field is locked.
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);

  // Resolve a worker-invite token into pre-populated details. Runs once on mount;
  // silently no-ops when there's no token or the token is invalid/expired.
  const inviteCancelledRef = useRef(false);
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite");
    if (!token) return;
    inviteCancelledRef.current = false;
    (async () => {
      try {
        const prefill = await getWorkerInvitePrefill(token);
        if (inviteCancelledRef.current) return;
        // Invites are for workers joining an existing restaurant's roster.
        methods.setValue("role", "worker");
        methods.setValue("workerInviteToken", token);
        if (prefill.name) {
          const parts = prefill.name.trim().split(/\s+/);
          methods.setValue("firstname", parts[0] ?? "");
          if (parts.length > 1) {
            methods.setValue("lastname", parts.slice(1).join(" "));
          }
        }
        if (prefill.email) {
          methods.setValue("email", prefill.email);
          setInvitedEmail(prefill.email);
        }
        if (prefill.phone) methods.setValue("phone", prefill.phone);
        setInvitedBy(prefill.restaurantName ?? null);
        setInvitePrefilled(true);
      } catch {
        // Invalid/expired token — fall back to a blank signup form.
      }
    })();
    return () => {
      inviteCancelledRef.current = true;
    };
  }, [methods]);

  // "I don't have an invite code" flow: capture the email so we can reach out.
  const [interestMode, setInterestMode] = useState(false);
  const [interestEmail, setInterestEmail] = useState("");
  const [interestStatus, setInterestStatus] = useState<
    "idle" | "loading" | "done" | "error"
  >("idle");

  const openInterest = () => {
    setInterestEmail(methods.getValues("email") || "");
    setInterestStatus("idle");
    setInterestMode(true);
  };

  const submitInterest = async (e: React.FormEvent) => {
    e.preventDefault();
    setInterestStatus("loading");
    try {
      const role = methods.getValues("role");
      const res = await fetch(`${BASE_URL}/public/express-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: interestEmail.trim(), role }),
      });
      if (!res.ok) throw new Error("Request failed");
      setInterestStatus("done");
    } catch {
      setInterestStatus("error");
    }
  };

  // Funnel step 1 (T2 / APPENDIX §B): fire `{role}_signup_started` on the first
  // meaningful interaction — here, the first focus of any signup field. Tracked
  // per role so toggling worker/employer can start each funnel at most once,
  // and never fires twice for a single user action.
  const startedFiredFor = useRef<Set<"worker" | "employer">>(new Set());
  const handleSignupFieldFocus = () => {
    const role = methods.getValues("role");
    if (startedFiredFor.current.has(role)) return;
    startedFiredFor.current.add(role);
    captureEvent(signupStartedEvent(role));
  };

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      const role = auth.user.role;
      const nextRoute = decideNextRoute({
        role,
        emailVerified: auth.user.emailVerified,
        phoneVerified: auth.user.phoneVerified,
        onboardingCompleted: auth.user.onboardingCompleted,
      });
      if (nextRoute.startsWith("/app/")) {
        navigate({ to: nextRoute, params: { role } });
      } else {
        navigate({ to: nextRoute });
      }
    }
  }, [auth.isAuthenticated, auth.user, navigate]);
  const handleSubmit = methods.handleSubmit(async (data) => {
    const parse = SignupSchema.safeParse(data);
    if (!parse.success) {
      setError(t("signup.errorCheckFields"));
      return;
    }

    try {
      // smsConsent is a client-side opt-in, not a registration field — strip it
      // before registering and record it via the preferences endpoint after.
      const { smsConsent, ...registerData } = parse.data;
      await auth.register(registerData);
      // Funnel step 2 (T2 / APPENDIX §B): credentials submitted, Keycloak user
      // created. `parse.data.role` is the authoritative submitted role.
      captureEvent(signupAccountCreatedEvent(parse.data.role));
      if (smsConsent) {
        // Best-effort: record SMS opt-in. Never block/break signup if it fails.
        try {
          await updateCommunicationPreferences({
            sms_updates: true,
            email_updates: true,
          });
        } catch {
          // ignore — the user can manage this later in Communication settings
        }
      }
      setError("");
    } catch (err: unknown) {
      let message = t("signup.error");
      if (err instanceof Error) {
        if (
          err.message.toLowerCase().includes("already exists") ||
          err.message.includes("User exists with same email") ||
          err.message.includes("409")
        ) {
          message = t("signup.errorEmailExists");
        } else if (err.message.includes("invitation was sent to")) {
          message = t("signup.errorInviteEmailMismatch");
        } else if (err.message.toLowerCase().includes("invite")) {
          message = t("signup.errorInviteInvalid");
        }
      }
      setError(message);
    }
  });

  return (
    <div className="max-w-[920px]">
      <h1 className="heading-1 mb-5">{t("signup.heading")}</h1>
      {invitePrefilled && (
        <div className="mb-6 max-w-[660px] rounded-lg border-2 border-ember/30 bg-ember/5 p-4">
          <p className="body-small">
            {invitedBy
              ? t("signup.invitedBy", { restaurant: invitedBy })
              : t("signup.invitedGeneric")}
          </p>
        </div>
      )}
      <FormProvider {...methods}>
        {!invitePrefilled && (
          <>
            <ToggleGroup
              type="single"
              defaultValue="worker"
              className="mb-6"
              onValueChange={(value: "worker" | "employer") =>
                methods.setValue("role", value)
              }
            >
              <ToggleGroupItem value="worker">
                <img
                  src="/worker.svg"
                  alt=""
                  className="h-[28px] w-[34px]"
                  aria-hidden="true"
                  width={28}
                  height={34}
                />
                <span>{t("signup.workerToggle")}</span>
              </ToggleGroupItem>
              <ToggleGroupItem value="employer">
                <img
                  src="/employer.svg"
                  alt=""
                  className="h-[28px] w-[34px]"
                  aria-hidden="true"
                  width={28}
                  height={34}
                />
                <span>{t("signup.employerToggle")}</span>
              </ToggleGroupItem>
            </ToggleGroup>
            <div className="mb-6 border-b border-black/10 pb-4">
              <p className="body-small">
                {t("signup.notSure")}{" "}
                <Link to="/static/quick-help" className="underline">
                  {t("signup.getHelp")}
                </Link>
              </p>
            </div>
          </>
        )}
        {REQUIRE_INVITE_CODE && interestMode && (
          <div className="mb-6 max-w-[520px] rounded-lg border border-black/10 p-5">
            <h2 className="heading-3 mb-2">{t("signup.interestHeading")}</h2>
            <p className="body-small mb-4">{t("signup.interestBlurb")}</p>
            {interestStatus === "done" ? (
              <p className="body-small text-green-600">
                {t("signup.interestSuccess")}
              </p>
            ) : (
              <form className="flex flex-col gap-3" onSubmit={submitInterest}>
                <div className="w-full md:w-[320px]">
                  <Input
                    type="email"
                    label={t("signup.interestEmailLabel")}
                    value={interestEmail}
                    onChange={(e) => setInterestEmail(e.target.value)}
                    required
                  />
                </div>
                {interestStatus === "error" && (
                  <p className="body-small text-red-500">
                    {t("signup.interestError")}
                  </p>
                )}
                <div className="mt-1 flex items-center gap-4">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={interestStatus === "loading"}
                  >
                    {interestStatus === "loading"
                      ? t("signup.interestSending")
                      : t("signup.interestSubmit")}
                  </Button>
                  <button
                    type="button"
                    className="underline body-small"
                    onClick={() => setInterestMode(false)}
                  >
                    {t("signup.backToSignup")}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
        <form
          className={`flex flex-col gap-4 ${interestMode ? "hidden" : ""}`}
          onSubmit={handleSubmit}
          onFocus={handleSignupFieldFocus}
        >
          <input type="hidden" name="role" value="worker" />
          {REQUIRE_INVITE_CODE && !invitePrefilled && (
            <div className="max-w-[520px] rounded-lg border-2 border-ember/30 bg-ember/5 p-5">
              <div className="mb-3 flex items-center gap-2">
                <span
                  className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-ember text-[13px] font-bold text-white"
                  aria-hidden="true"
                >
                  🔑
                </span>
                <h2 className="heading-3">
                  {t("signup.inviteCodePanelHeading")}
                </h2>
              </div>
              <p className="body-small mb-4 text-slate">
                {t("signup.inviteCodePanelBlurb")}
              </p>
              <div className="flex flex-col gap-2">
                <div className="w-full md:w-[320px]">
                  <Input
                    {...methods.register("inviteCode")}
                    label={t("signup.inviteCodeLabel")}
                    placeholder={t("signup.inviteCodePlaceholder")}
                    state={
                      methods.formState.errors.inviteCode ? "error" : "default"
                    }
                    hint={methods.formState.errors.inviteCode?.message}
                    className="bg-white!"
                    required
                  />
                </div>
                <button
                  type="button"
                  className="underline body-small self-start"
                  onClick={openInterest}
                >
                  {t("signup.noInviteCode")}
                </button>
              </div>
            </div>
          )}
          {REQUIRE_INVITE_CODE && !invitePrefilled && (
            <h2 className="heading-3 mt-2">{t("signup.yourDetailsHeading")}</h2>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_320px]">
            <Input
              {...methods.register("firstname")}
              label={t("signup.firstName")}
              placeholder={t("signup.firstNamePlaceholder")}
              state={methods.formState.errors.firstname ? "error" : "default"}
              hint={methods.formState.errors.firstname?.message}
              required
            />
            <Input
              {...methods.register("lastname")}
              label={t("signup.lastName")}
              state={methods.formState.errors.lastname ? "error" : "default"}
              hint={methods.formState.errors.lastname?.message}
              required
            />
            <Input
              {...methods.register("email")}
              type="email"
              label={t("signup.emailLabel")}
              state={methods.formState.errors.email ? "error" : "default"}
              hint={
                methods.formState.errors.email?.message ??
                (invitedEmail ? t("signup.invitedEmailHint") : undefined)
              }
              readOnly={!!invitedEmail}
              className={invitedEmail ? "bg-fog-dark" : undefined}
              required
            />
            <Input
              {...methods.register("phone")}
              label={t("signup.phoneLabel")}
              state={methods.formState.errors.phone ? "error" : "default"}
              hint={methods.formState.errors.phone?.message}
              required
            />
            <PasswordInput
              {...methods.register("password")}
              label={t("signup.passwordLabel")}
              placeholder={t("signup.passwordPlaceholder")}
              state={methods.formState.errors.password ? "error" : "default"}
              hint={methods.formState.errors.password?.message}
              required
            />
            <PasswordInput
              {...methods.register("password2")}
              label={t("signup.repeatPasswordLabel")}
              placeholder={t("signup.passwordPlaceholder")}
              state={methods.formState.errors.password2 ? "error" : "default"}
              hint={methods.formState.errors.password2?.message}
              required
            />
          </div>
          <p className="mt-1 body-small">
            {t("signup.termsBlurbPrefix")}
            <Link
              to="/static/privacy"
              target="_blank"
              className="underline font-bold text-inherit"
            >
              {t("signup.privacyPolicy")}
            </Link>
            {t("signup.termsBlurbMiddle")}
            <Link
              to="/static/terms"
              target="_blank"
              className="underline font-bold text-inherit"
            >
              {t("signup.termsOfService")}
            </Link>
          </p>
          <label
            htmlFor="sms-consent"
            className="flex flex-row gap-3 items-start max-w-[660px] p-3 rounded-lg ring-1 ring-black/10 cursor-pointer"
          >
            <Controller
              control={methods.control}
              name="smsConsent"
              render={({ field }) => (
                <Checkbox
                  id="sms-consent"
                  checked={Boolean(field.value)}
                  onCheckedChange={(v) => field.onChange(Boolean(v))}
                  className="size-5 mt-0.5 shrink-0 border-2 border-garnet data-[state=checked]:bg-garnet data-[state=checked]:text-white"
                />
              )}
            />
            <span className="body-xs text-slate flex-1 leading-snug">
              {t("signup.smsConsentLabel")}{" "}
              <Link
                to="/static/terms"
                target="_blank"
                className="underline font-semibold text-inherit"
              >
                {t("signup.termsOfService")}
              </Link>
              {" & "}
              <Link
                to="/static/privacy"
                target="_blank"
                className="underline font-semibold text-inherit"
              >
                {t("signup.privacyPolicy")}
              </Link>
              .
            </span>
          </label>
          <div className="mt-2">
            <Button variant="primary" className="w-[320px]" type="submit">
              {t("signup.submit")}
            </Button>
          </div>
          {error && (
            <p className="body-small text-red-500">
              {error.toLowerCase().includes("already exists") ||
              error === t("signup.errorEmailExists") ? (
                <>
                  {t("signup.errorEmailExistsShort")}{" "}
                  <Link
                    className="underline font-bold text-red-500"
                    to="/auth/login"
                  >
                    {t("signup.logInInstead")}
                  </Link>
                  .
                </>
              ) : (
                error
              )}
            </p>
          )}
          <p className="mt-2 body-small">
            {t("signup.haveAccount")}{" "}
            <Link className="underline font-bold" to="/auth/login">
              {t("signup.loginLinkCaps")}
            </Link>
          </p>
        </form>
      </FormProvider>
    </div>
  );
}
